import React from "react";
import { Box, IconButton, InputAdornment, TextField } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useFormik } from "formik";
import * as Yup from "yup";

import { useServices, useSnackbar } from "contexts";
import { changePassword } from "services";

import { fieldSx, FieldLabel, ErrorText } from "../../components/FormFields";

/**
 * Change-password form — the mobile port of `components/Profile/ChangePassword`.
 * Same `changePassword` call and the same validation rules; the three fields are
 * stacked instead of desktop's current-password-then-two-across layout.
 */

const Schema = new Yup.ObjectSchema({
  currentPassword: Yup.string().required("Required"),
  newPassword: Yup.string()
    .min(8, "Min 8 characters required")
    .max(50, "Max 50 characters allowed")
    .notOneOf(
      [Yup.ref("currentPassword"), null],
      "New Password has to be different than current Password"
    )
    .required("Required"),
  confirmedPassword: Yup.string()
    .min(8, "Min 8 characters required")
    .max(50, "Max 50 characters allowed")
    .oneOf([Yup.ref("newPassword"), null], "Does not match with new password!")
    .required("Required"),
});

const FIELDS = [
  { name: "currentPassword", label: "Current Password" },
  { name: "newPassword", label: "New Password" },
  { name: "confirmedPassword", label: "Confirm Password" },
];

export default function ChangePassword() {
  const service = useServices();
  const snackbar = useSnackbar();

  const [loading, setLoading] = React.useState(false);
  const [visible, setVisible] = React.useState({});

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmedPassword: "",
    },
    validationSchema: Schema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);

        await service.callService(changePassword, values);

        snackbar.success("Password updated successfully!");
        resetForm();
      } catch (error) {
        console.error(error);
        snackbar.error(error.message);
      } finally {
        setLoading(false);
      }
    },
  });

  const toggleVisible = (name) =>
    setVisible((current) => ({ ...current, [name]: !current[name] }));

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      noValidate
      sx={{ display: "flex", flexDirection: "column", gap: 5 }}
    >
      {FIELDS.map(({ name, label }) => {
        const error =
          formik.touched[name] && !!formik.errors[name]
            ? formik.errors[name]
            : "";

        return (
          <Box key={name}>
            <FieldLabel>{label}</FieldLabel>
            <TextField
              id={name}
              name={name}
              placeholder="xxxxxxx"
              fullWidth
              size="small"
              spellCheck="false"
              type={visible[name] ? "text" : "password"}
              value={formik.values[name]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              sx={(theme) => fieldSx(theme, !!error)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={`toggle ${label} visibility`}
                      onClick={() => toggleVisible(name)}
                      onMouseDown={(e) => e.preventDefault()}
                      size="small"
                    >
                      {visible[name] ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && <ErrorText>{error}</ErrorText>}
          </Box>
        );
      })}

      <LoadingButton type="submit" variant="contained" loading={loading} fullWidth>
        Update Password
      </LoadingButton>
    </Box>
  );
}
